import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Coffee, Settings, Lock, ShoppingBag, Edit3, BarChart3, TrendingUp, DollarSign, ArrowLeft } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #000000;
  padding: 40px 20px;
`;

const PageHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 40px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  background: transparent;
  color: #e0c16f;
  border: 1px solid #e0c16f;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  font-family: var(--font-heading);
  transition: all 0.2s ease;
  &:hover { background: rgba(224, 193, 111, 0.1); }
`;

const PageTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: 3rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
`;

const PageSubtitle = styled.p`
  font-family: var(--font-body);
  font-size: 1.2rem;
  color: #aaaaaa;
  margin-bottom: 32px;
`;

const ContentCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 48px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(95.36deg, rgba(224, 193, 111, 0.1) 5.18%, rgba(189, 133, 27, 0.1) 93.21%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #e0c16f;
  svg { width: 40px; height: 40px; }
`;

const ComingSoonTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
`;

const ComingSoonText = styled.p`
  font-family: var(--font-body);
  color: #aaaaaa;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ActionButton = styled.button`
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  color: #000000;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(224, 193, 111, 0.3); }
`;

const CreatorPage = () => {
  const { section } = useParams();
  const navigate = useNavigate();

  const getPageContent = () => {
    switch (section) {
      case 'payouts':
        return {
          icon: <DollarSign />,
          title: 'Payouts & Earnings',
          subtitle: 'Track your revenue and withdraw funds',
          description: 'Manage your bank accounts, view transaction history, and request payouts from your BUCX balance.',
          actionText: 'Setup Payouts',
          action: () => alert('Payout setup coming soon!')
        };
      case 'buttons-graphics':
        return {
          icon: <Edit3 />,
          title: 'Buttons & Graphics',
          subtitle: 'Promote your BUCX page',
          description: 'Download official BUCX buttons, banners, and graphics to use on your website, streams, and social media.',
          actionText: 'Download Assets',
          action: () => alert('Assets downloading coming soon!')
        };
      case 'integrations':
        return {
          icon: <Settings />,
          title: 'Integrations',
          subtitle: 'Connect your favorite tools',
          description: 'Link your BUCX account with Discord, Telegram, YouTube, and other platforms to automate benefits.',
          actionText: 'Explore Integrations',
          action: () => alert('Integrations coming soon!')
        };
      default:
        return {
          icon: <Coffee />,
          title: 'Coming Soon',
          subtitle: 'This feature is under development',
          description: "We're working hard to bring you this amazing feature. Check back soon for updates and new functionality.",
          actionText: 'Go Back',
          action: () => navigate('/creator-dashboard')
        };
    }
  };

  const content = getPageContent();

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/creator-dashboard')}>
          <ArrowLeft size={18} /> Dashboard
        </BackButton>
        <PageTitle>{content.title}</PageTitle>
        <PageSubtitle>{content.subtitle}</PageSubtitle>
      </PageHeader>

      <ContentCard>
        <IconContainer>{content.icon}</IconContainer>
        <ComingSoonTitle>{content.title}</ComingSoonTitle>
        <ComingSoonText>{content.description}</ComingSoonText>
        <ActionButton onClick={content.action}>{content.actionText}</ActionButton>
      </ContentCard>
    </PageContainer>
  );
};

export default CreatorPage;